/**
 * LazyMount — defer mounting expensive children until the slot first scrolls
 * near the viewport, then keep them mounted.
 *
 * Why: the compare strip mounts up to 100 columns
 * at once (CompareKeywordPage). Each PDF column mounts a RegulatoryViewerEmbed
 * that eagerly fetches all pages + text spans on mount, so 50 columns fire
 * hundreds of concurrent requests and freeze the tab. Gating each column behind
 * an IntersectionObserver means only the columns the user actually scrolls to
 * pay that cost.
 *
 * Once revealed a slot stays mounted (the observer disconnects): re-hiding it on
 * scroll-away would unmount the viewer and re-fetch everything the next time it
 * scrolls back — trading one freeze for many.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

export function LazyMount({
  children,
  placeholder = null,
  rootMargin = '600px',
  className,
  style,
}: {
  children: ReactNode;
  /** Rendered in place of children until first reveal (keep it cheap). */
  placeholder?: ReactNode;
  /** Prefetch margin — reveal a slot slightly before it enters the viewport. */
  rootMargin?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;
    const el = ref.current;
    if (!el) return;
    // Environments without IntersectionObserver (very old browsers, some test
    // harnesses) can't gate — render eagerly rather than never.
    if (typeof IntersectionObserver === 'undefined') { setShown(true); return; }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) { setShown(true); io.disconnect(); }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, rootMargin]);

  return (
    <div ref={ref} className={className} style={style}>
      {shown ? children : placeholder}
    </div>
  );
}
